import isEmpty from "../../../../common/helpers/isEmpty";
import { NameIdOption } from "../../../../common/types/commonTypes";
import { Nullable } from "../../../../common/types/nullable";
import { getCoreUserFullName } from "../../../../common/utils/users";
import { InvoiceLanguage } from "../../../invoices/invoiceAPI";
import { OrderQueue } from "../../../orders/orderAPI";
import { SalesAccount } from "../../../settings/settingsApi";
import { CustomerContract, Factoring } from "../../customerAPI";
import { CustomerOrganizationsResult, CustomerUser } from "../../types/queries";
import { InvoiceSeparation, YesNoOptions } from "../constants";
import { formatDateForBE } from "./commonUtils";

export const emptyCustomerContract: CustomerContract = {
  id: "",
  customerCompanyId: "",
  eezyCompanyId: "",
  name: "",
  version: 0,
  salesAccount: {
    code: "",
    canUseInContract: false,
    canUseInGroupInternalSales: false,
    defaultVatPercent: "",
    description: "",
    defaultVatCode: "",
    canUseInExternalSales: false,
  },
  schedulingClassId: "",
  monthlyWorkSchedulingClassId: "",
  invoiceFormat: "",
  invoiceBreakdown: [""],
  invoiceReferences: {
    agreementIdentifier: "",
    orderIdentifier: "",
    sellerReferenceIdentifier: "",
  },
  paymentTermDays: 0,
  salesPersonId: "",
  salesPersonName: "",
  invoicesByCostCenter: false,
  invoiceDeliveryDate: false,
  eezyCompany: { id: "", name: "", businessId: "" },
  customerOrgId: [],
  invoiceLanguage: InvoiceLanguage.FI,
};

export const getRepresentativeAndSubOrganizations = (
  orgArray: CustomerOrganizationsResult[],
) => {
  let repOrg: CustomerOrganizationsResult | null = null;
  let subOrgs: CustomerOrganizationsResult[] = [];

  orgArray.forEach((org) => {
    if (org.isRepresentative) return (repOrg = org);
    subOrgs = [...subOrgs, org];
  });
  return { repOrg, subOrgs };
};

export const handleCustomerOrganizationsDataForForm = ({
  corgIdList,
  customerOrganizations,
}: {
  corgIdList?: string[];
  customerOrganizations: CustomerOrganizationsResult[];
}) => {
  if (!corgIdList || corgIdList.length === 0) return [];
  const corgIdNameList = corgIdList.map((corgId) => {
    const foundOrg = customerOrganizations.find((org) => org.id === corgId);
    if (foundOrg) {
      return {
        id: corgId,
        name: foundOrg.name,
      };
    }
    return {
      id: corgId,
      name: corgId,
    };
  });
  return corgIdNameList;
};

export const handleOrderQueueDataForForm = ({
  orderQueueId,
  orderQueues,
}: {
  orderQueueId?: string;
  orderQueues: OrderQueue[];
}) => {
  if (!orderQueueId) return null;
  const foundOrderQueue = orderQueues.find(
    (orderQueue) => orderQueue.id === orderQueueId,
  );
  if (foundOrderQueue) {
    return {
      id: orderQueueId,
      name: foundOrderQueue.name,
    };
  }
  return {
    id: orderQueueId,
    name: orderQueueId,
  };
};

export const handleExpertUserDataForForm = ({
  userId,
  userName,
  expertUserOptions,
}: {
  userId?: string;
  userName?: string;
  expertUserOptions: NameIdOption[];
}) => {
  if (!userId) return null;
  if (userName) return { id: userId, name: userName };
  const option = expertUserOptions.find((user) => user.id === userId);

  const name = option?.name;

  // If we don't have a full name to show, we don't have a valid user. Return null.
  if (!name) return null;

  return {
    id: userId,
    name,
  };
};

export const handleCustomerUserDataForForm = ({
  userId,
  customerUsers,
}: {
  userId?: string;
  customerUsers: CustomerUser[];
}) => {
  if (!userId) return null;
  const customerUser = customerUsers.find((user) => user.id === userId);

  const fullName = customerUser?.account && getCoreUserFullName(customerUser);

  // If we don't have a full name to show, we don't have a valid user. Return null.
  if (!fullName) return null;

  return {
    id: userId,
    name: fullName,
  };
};

export const handleInvoicesSeparation = (
  invoicesByCC?: boolean,
  invoicesByWorkLocation?: boolean,
): Nullable<InvoiceSeparation> => {
  if (invoicesByCC && invoicesByWorkLocation) return null; // some kind of error has happened
  if (!invoicesByCC && !invoicesByWorkLocation) return InvoiceSeparation.SINGLE;
  if (invoicesByCC) return InvoiceSeparation.BY_COST_CENTER;
  if (invoicesByWorkLocation) return InvoiceSeparation.BY_WORK_LOCATION;

  return null;
};

export const handleFactoringSalesAccountDataForForm = ({
  factoringSalesAccountCode,
  salesAccountsOptions,
}: {
  factoringSalesAccountCode?: string;
  salesAccountsOptions: SalesAccount[];
}) => {
  if (factoringSalesAccountCode) return factoringSalesAccountCode;

  // Whimsical spec
  const DEFAULT_FACTORING_SALES_ACCOUNT_CODE = "300000";

  // Make sure the default code is in the list of options
  const foundSalesAccount = salesAccountsOptions.find(
    (salesAccount) =>
      salesAccount.code === DEFAULT_FACTORING_SALES_ACCOUNT_CODE,
  );
  if (foundSalesAccount) {
    return foundSalesAccount.code;
  }

  return "";
};

export const handleContractDataForApi = (contractData: any, formData: any) => {
  const { id: contractId, version: contractVersion } = contractData;

  const handleFactoringData = (formData: any) => {
    if (formData.factoringEnabled === YesNoOptions.Yes) {
      return {
        factoringContractId: formData.factoringContractId,
        salesAccount: formData.factoringSalesAccountCode,
        customerOrgIds: formData.factoringCustomerOrgIds.map(
          (org: any) => org.id,
        ),
      } as Factoring;
    }
    return null;
  };

  const getInvoiceReferences = (formData: any) => {
    return {
      ...(formData.orderIdentifier && {
        orderIdentifier: formData.orderIdentifier,
      }),
      ...(formData.agreementIdentifier && {
        agreementIdentifier: formData.agreementIdentifier,
      }),
      ...(formData.sellerReferenceIdentifier && {
        sellerReferenceIdentifier: formData.sellerReferenceIdentifier,
      }),
      ...(formData.buyerReferenceIdentifier && {
        buyerReferenceIdentifier: formData.buyerReferenceIdentifier,
      }),
    };
  };

  const getInvoicesSeparations = (formData: any) => {
    const separationMap = {
      [InvoiceSeparation.BY_COST_CENTER]: {
        invoicesByCostCenter: true,
        invoicesByWorkLocation: false,
      },
      [InvoiceSeparation.BY_WORK_LOCATION]: {
        invoicesByCostCenter: false,
        invoicesByWorkLocation: true,
      },
      [InvoiceSeparation.SINGLE]: {
        invoicesByCostCenter: false,
        invoicesByWorkLocation: false,
      },
    };

    return separationMap[formData.invoicesSeparation as InvoiceSeparation];
  };

  return {
    id: contractId,
    version: contractVersion,
    validFrom: !isEmpty(formData.validFromDate)
      ? formatDateForBE(formData.validFromDate)
      : null,
    customerOrgId: formData.customerOrgId.map((org: any) => org.id),
    salesPersonId: formData.salesPerson.id,
    salesPersonName: formData.salesPerson?.name,
    ...(formData.orderQueue?.id && {
      orderQueueId: formData.orderQueue.id,
    }),
    ...(formData.contractResponsiblePerson?.id && {
      contractResponsiblePerson: {
        id: formData.contractResponsiblePerson.id,
      },
    }),
    ...(formData.invoicingResponsiblePerson?.id && {
      invoicingResponsiblePerson: {
        id: formData.invoicingResponsiblePerson.id,
      },
    }),
    ...(!isEmpty(formData.paymentTermDays, true) && {
      paymentTermDays: Number(formData.paymentTermDays),
    }),
    ...(formData.invoiceFormat && {
      invoiceFormat: formData.invoiceFormat,
    }),
    invoiceBreakdown: formData.invoiceBreakdown,
    ...(formData.salesAccountCode && {
      salesAccount: formData.salesAccountCode,
    }),
    factoring: handleFactoringData(formData),
    invoiceReferences: getInvoiceReferences(formData),
    //  invoicesByCostCenter & invoicesByWorkLocation
    ...getInvoicesSeparations(formData),
    invoiceDeliveryDate:
      formData.invoiceDeliveryDateEnabled === YesNoOptions.Yes,
    invoiceLanguage: formData.invoiceLanguage,
  };
};
